import React, { useEffect } from 'react';
import {
    CheckCircle2,
    Film,
    Image,
    Play,
    Smartphone,
    Palette,
    Volume2,
    Type,
    Layers,
    Clock,
    Send,
    Mic2,
    Sparkles,
    Package,
    CalendarClock,
    MessageSquare,
    FolderOpen,
    TrendingUp,
    Wand2,
    Zap
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
    <div className={`rounded-2xl border border-white/10 bg-white/5 backdrop-blur-lg p-6 md:p-8 shadow-xl ${className}`}>
        {children}
    </div>
);

const CheckRow = ({ children }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-indigo-400" aria-hidden />
        <span className="text-white/80 leading-relaxed">{children}</span>
    </li>
);

const DeliverableCard = ({ icon: IconComponent, number, title, items, accent = "indigo" }) => {
    const accentClasses = {
        indigo: { bg: "bg-indigo-500/20", text: "text-indigo-400", muted: "text-indigo-400/60", hover: "hover:border-indigo-500/40" },
        amber: { bg: "bg-amber-500/20", text: "text-amber-400", muted: "text-amber-400/60", hover: "hover:border-amber-500/40" },
        emerald: { bg: "bg-emerald-500/20", text: "text-emerald-400", muted: "text-emerald-400/60", hover: "hover:border-emerald-500/40" },
        rose: { bg: "bg-rose-500/20", text: "text-rose-400", muted: "text-rose-400/60", hover: "hover:border-rose-500/40" },
        sky: { bg: "bg-sky-500/20", text: "text-sky-400", muted: "text-sky-400/60", hover: "hover:border-sky-500/40" }
    };
    const a = accentClasses[accent] || accentClasses.indigo;
    return (
        <Card className={`${a.hover} transition-all duration-300`}>
            <div className="flex items-center gap-3 mb-5">
                <div className={`p-2.5 rounded-xl ${a.bg} ${a.text}`}>
                    <IconComponent className="w-6 h-6" />
                </div>
                <div>
                    <span className={`text-xs font-bold uppercase tracking-widest ${a.muted}`}>
                        {String(number).padStart(2, '0')}
                    </span>
                    <h3 className="text-xl font-bold text-white leading-tight">{title}</h3>
                </div>
            </div>
            <ul className="space-y-3">
                {items.map((item, i) => (
                    <CheckRow key={i}>{item}</CheckRow>
                ))}
            </ul>
        </Card>
    );
};

const PillarHeader = ({ icon: IconComponent, label, color }) => (
    <div className="flex items-center gap-3 mb-6">
        <div className={`p-2 rounded-lg ${color}`}>
            <IconComponent className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-white tracking-tight">{label}</h3>
    </div>
);

export default function AbhiveerContentOps() {

    useEffect(() => {
        document.title = 'SocialSEO | Abhiveer Content-Ops Package';
    }, []);

    const production = [
        {
            icon: Film,
            number: 1,
            title: "Full Episode Edit (4K)",
            accent: "indigo",
            items: [
                "Rendered in 3840×2160 (4K UHD) — H.265 + H.264 exports",
                "Multi-camera sync and switching",
                "Clean cuts, dead air removal, filler word cleanup",
                "Smooth transitions, zoom-ins, B-roll integration"
            ]
        },
        {
            icon: Palette,
            number: 2,
            title: "Color Grading",
            accent: "indigo",
            items: [
                "White balance correction across all angles",
                "Consistent temperature between host and guest",
                "Cinematic LUT matched to brand aesthetic",
                "Skin tone correction, exposure balancing, scene continuity"
            ]
        },
        {
            icon: Volume2,
            number: 3,
            title: "Audio Mastering",
            accent: "indigo",
            items: [
                "Background noise removal (hiss, hum, room reverb)",
                "Loudness normalization to -14 LUFS (YouTube standard)",
                "Compression, EQ, de-essing for vocal clarity",
                "Consistent levels host ↔ guest across full episode"
            ]
        },
        {
            icon: Type,
            number: 4,
            title: "Graphics & Intro / Outro",
            accent: "indigo",
            items: [
                "Branded 16:9 intro bumper with guest name title card",
                "Lower thirds for host and guest (first appearance + periodic)",
                "Topic title cards for segment transitions",
                "Branded outro with subscribe + next-video end screen",
                "YouTube chapters — timestamped, copy-paste ready"
            ]
        }
    ];

    const packaging = [
        {
            icon: Image,
            number: 5,
            title: "YouTube Thumbnails (3 variants)",
            accent: "amber",
            items: [
                "3 unique compositions per episode for A/B/C testing",
                "Different hook angles, expressions, and layouts",
                "Custom guest face cutout, text overlay, brand colors",
                "Exported at 1280×720 + high-res source files"
            ]
        },
        {
            icon: Image,
            number: 6,
            title: "Trailer Thumbnails",
            accent: "amber",
            items: [
                "Dedicated vertical thumbnail for each trailer/reel",
                "Optimized for Instagram, TikTok, and Shorts preview cards",
                "Hook-led typography sized for mobile feed scanning"
            ]
        },
        {
            icon: Image,
            number: 7,
            title: "Podcast Platform Artwork",
            accent: "amber",
            items: [
                "Episode artwork sized for Apple Podcasts, Spotify, and major platforms",
                "Consistent brand system across all touch points",
                "Uploaded directly to platforms as part of asset handoff"
            ]
        }
    ];

    const distribution = [
        {
            icon: Smartphone,
            number: 8,
            title: "Vertical Trailer / Reel (9:16)",
            accent: "emerald",
            items: [
                "30–60 second hook-led cut per episode",
                "Auto-captioned with branded styling",
                "Designed for Instagram Reels, YouTube Shorts, TikTok",
                "Currently a missing piece in the existing content flow — net-new growth surface"
            ]
        },
        {
            icon: Wand2,
            number: 9,
            title: "SEO Captions via Poppy AI",
            accent: "emerald",
            items: [
                "Episode description with keywords, guest mentions, hook-led opening",
                "Per-trailer caption variants tuned for IG / Shorts / TikTok",
                "Built around guest context, episode topics, and target search intent"
            ]
        },
        {
            icon: CalendarClock,
            number: 10,
            title: "Scheduling on Later",
            accent: "emerald",
            items: [
                "Trailers scheduled to optimal day/time per platform",
                "Captions + thumbnails attached before publish",
                "Cross-platform queue managed end-to-end"
            ]
        }
    ];

    const automation = [
        {
            icon: MessageSquare,
            number: 11,
            title: "ManyChat Automation per Trailer",
            accent: "rose",
            items: [
                "Keyword-trigger DM flow set up for every published trailer",
                "Lead-magnet or episode-link delivery via DM",
                "Comment auto-reply layer to drive opt-ins",
                "Built fresh per episode — not a recycled template"
            ]
        }
    ];

    const operations = [
        {
            icon: FolderOpen,
            number: 12,
            title: "Per-Guest Master Folder System",
            accent: "sky",
            items: [
                "Dedicated folder per guest — full episode, reels, thumbnails, captions, exports all live together",
                "Naming convention enforced regardless of edit day",
                "Shared drive structured for instant handoff and future repurposing"
            ]
        },
        {
            icon: Package,
            number: 13,
            title: "Multi-Platform Upload Management",
            accent: "sky",
            items: [
                "Final assets pushed to YouTube, IG, TikTok, podcast hosts",
                "Episode-level checklist tracked from edit lock to live",
                "Single point of accountability for the full ship"
            ]
        }
    ];

    const requirements = [
        { item: "Raw footage", note: "All camera angles, highest quality available" },
        { item: "Audio files", note: "Separate tracks per speaker (lav / interface)" },
        { item: "Brand kit", note: "Logo (PNG/SVG), brand colors, fonts" },
        { item: "Guest info", note: "Name, title, social handles, headshot" },
        { item: "Tool access", note: "ManyChat + Poppy AI + Later (we already have Later)" },
        { item: "Shared drive", note: "Workspace where master folders live" }
    ];

    return (
        <div className="min-h-screen bg-transparent font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24">

            {/* Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white">Social<span className="text-indigo-400">SEO</span></span>
                </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-24">

                {/* Hero */}
                <section className="text-center pt-10">
                    <div className="inline-flex items-center gap-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full px-4 py-1.5 mb-8">
                        <Mic2 className="w-4 h-4 text-indigo-400" />
                        <span className="text-sm font-medium text-indigo-300">Full Content-Ops Package</span>
                    </div>
                    <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400">
                        SocialSEO × Abhiveer
                    </h1>
                    <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto leading-relaxed">
                        End-to-end podcast content operations. Production, packaging, distribution, automation, and asset management — one team owns the entire pipeline.
                    </p>
                </section>

                {/* The Shift */}
                <section>
                    <Card className="max-w-4xl mx-auto border-indigo-500/20 bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 relative overflow-hidden">
                        <div className="absolute -right-6 -top-6 text-[120px] font-black text-indigo-500/[0.06] pointer-events-none leading-none">→</div>
                        <div className="flex items-start gap-5">
                            <div className="bg-indigo-500/20 p-3 rounded-2xl flex-shrink-0 mt-1">
                                <TrendingUp className="w-8 h-8 text-indigo-400" />
                            </div>
                            <div>
                                <h3 className="text-2xl md:text-3xl font-black text-white mb-3">
                                    From Editing Service → Content Engine
                                </h3>
                                <p className="text-white/70 text-lg leading-relaxed mb-4">
                                    Editing is now roughly 30% of the work. The rest is what turns one recording into a multi-platform content engine — trailers, thumbnails across every platform, SEO captions, scheduling, ManyChat automation, and per-guest asset organization. <strong className="text-indigo-300">Your main channel is currently not posting vertical trailers</strong> — that's a top-of-funnel surface this package activates from day one.
                                </p>
                                <div className="grid grid-cols-5 gap-2 mt-6">
                                    {[
                                        { icon: Film, label: "Production" },
                                        { icon: Image, label: "Packaging" },
                                        { icon: Smartphone, label: "Distribution" },
                                        { icon: Zap, label: "Automation" },
                                        { icon: FolderOpen, label: "Ops" }
                                    ].map((p, i) => (
                                        <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-3 text-center">
                                            <p.icon className="w-5 h-5 text-indigo-400 mx-auto mb-1" />
                                            <p className="text-xs text-white/60 font-semibold">{p.label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Card>
                </section>

                {/* Pricing */}
                <section id="pricing">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4">Monthly Retainer</h2>
                        <p className="text-white/50 text-lg">Same deliverables. Same quality. Commit to 3 months and save 20%.</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                        {/* Monthly */}
                        <Card className="flex flex-col border-t-4 border-t-slate-400 text-center">
                            <div className="mb-6 border-b border-white/10 pb-6">
                                <h3 className="text-2xl font-bold mb-2">Monthly</h3>
                                <p className="text-white/50 text-sm">Pay month-to-month, full flexibility</p>
                            </div>
                            <div className="mb-2">
                                <span className="text-5xl font-black text-white">$1,400</span>
                                <span className="text-white/50 font-medium ml-1">/ month</span>
                            </div>
                            <p className="text-white/40 text-sm mb-8">$350 per episode × 4 episodes</p>
                            <div className="mt-auto">
                                <ul className="space-y-3 text-left">
                                    <CheckRow>4 full episode edits (4K, color, audio, graphics)</CheckRow>
                                    <CheckRow>1 vertical trailer/reel per episode</CheckRow>
                                    <CheckRow>YouTube thumbnails — 3 variants per episode</CheckRow>
                                    <CheckRow>Trailer + podcast platform artwork</CheckRow>
                                    <CheckRow>SEO captions via Poppy AI</CheckRow>
                                    <CheckRow>Later scheduling + ManyChat automation</CheckRow>
                                    <CheckRow>Per-guest folder system + upload management</CheckRow>
                                </ul>
                            </div>
                        </Card>

                        {/* 3-Month Plan */}
                        <Card className="flex flex-col border-t-4 border-t-indigo-500 bg-indigo-500/10 transform md:-translate-y-4 shadow-[0_0_50px_-15px_rgba(99,102,241,0.3)] relative text-center">
                            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-widest shadow-lg flex items-center gap-1">
                                Best Value <Sparkles className="w-3 h-3" />
                            </div>
                            <div className="mb-6 border-b border-white/10 pb-6 mt-2">
                                <h3 className="text-2xl font-bold mb-2">3-Month Plan</h3>
                                <p className="text-white/50 text-sm">Commit to 3 months, save 20%</p>
                            </div>
                            <div className="mb-2">
                                <span className="text-5xl font-black text-white">$1,120</span>
                                <span className="text-white/50 font-medium ml-1">/ month</span>
                            </div>
                            <div className="mb-8">
                                <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full px-3 py-1 mt-2">
                                    <span className="text-sm font-bold text-emerald-400">$3,360 total — save $840</span>
                                </div>
                            </div>
                            <div className="mt-auto">
                                <ul className="space-y-3 text-left">
                                    <CheckRow>Everything in the Monthly plan</CheckRow>
                                    <CheckRow><strong className="text-white">20% off</strong> the standard rate</CheckRow>
                                    <CheckRow>Locked-in rate for 3 months</CheckRow>
                                    <CheckRow>Priority on rush requests</CheckRow>
                                </ul>
                            </div>
                        </Card>
                    </div>

                    <p className="text-center text-white/40 text-sm mt-8 max-w-2xl mx-auto">
                        Extra clips beyond the 1 trailer per episode, additional episodes, or out-of-scope assets are billed per unit and quoted in advance.
                    </p>
                </section>

                {/* What's Included — Organized by Pillar */}
                <section>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4 flex items-center justify-center gap-3">
                            <Package className="w-8 h-8 text-indigo-400" />
                            What's Included
                        </h2>
                        <p className="text-white/50 text-lg max-w-2xl mx-auto">
                            13 deliverables organized across five pillars — every episode.
                        </p>
                    </div>

                    {/* Production */}
                    <div className="mb-12">
                        <PillarHeader icon={Film} label="1. Production" color="bg-indigo-500" />
                        <div className="grid md:grid-cols-2 gap-6">
                            {production.map((d, i) => (
                                <DeliverableCard key={i} {...d} />
                            ))}
                        </div>
                    </div>

                    {/* Packaging */}
                    <div className="mb-12">
                        <PillarHeader icon={Image} label="2. Packaging — Multi-Platform Thumbnails" color="bg-amber-500" />
                        <div className="grid md:grid-cols-3 gap-6">
                            {packaging.map((d, i) => (
                                <DeliverableCard key={i} {...d} />
                            ))}
                        </div>
                    </div>

                    {/* Distribution */}
                    <div className="mb-12">
                        <PillarHeader icon={Smartphone} label="3. Distribution — Trailer + Captions + Scheduling" color="bg-emerald-500" />
                        <div className="grid md:grid-cols-3 gap-6">
                            {distribution.map((d, i) => (
                                <DeliverableCard key={i} {...d} />
                            ))}
                        </div>
                    </div>

                    {/* Automation */}
                    <div className="mb-12">
                        <PillarHeader icon={Zap} label="4. Automation — ManyChat Funnel" color="bg-rose-500" />
                        <div className="grid md:grid-cols-2 gap-6">
                            {automation.map((d, i) => (
                                <DeliverableCard key={i} {...d} />
                            ))}
                        </div>
                    </div>

                    {/* Operations */}
                    <div className="mb-4">
                        <PillarHeader icon={FolderOpen} label="5. Operations — Per-Guest Asset System" color="bg-sky-500" />
                        <div className="grid md:grid-cols-2 gap-6">
                            {operations.map((d, i) => (
                                <DeliverableCard key={i} {...d} />
                            ))}
                        </div>
                    </div>
                </section>

                {/* Trailer Highlight */}
                <section>
                    <Card className="max-w-4xl mx-auto border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 relative overflow-hidden">
                        <div className="absolute -right-6 -top-6 text-[120px] font-black text-emerald-500/[0.06] pointer-events-none leading-none">↑</div>
                        <div className="flex items-start gap-5">
                            <div className="bg-emerald-500/20 p-3 rounded-2xl flex-shrink-0 mt-1">
                                <Play className="w-8 h-8 text-emerald-400" />
                            </div>
                            <div>
                                <h3 className="text-2xl md:text-3xl font-black text-white mb-3">
                                    The Trailer Is the Unlock
                                </h3>
                                <p className="text-white/70 text-lg leading-relaxed">
                                    The main channel currently doesn't post trailers — which means none of the long-form views compound into a top-of-funnel reel surface. Every episode we ship will leave the studio with a vertical trailer + Manychat trigger live in the feed. <strong className="text-emerald-300">That's a content surface that doesn't exist today.</strong>
                                </p>
                            </div>
                        </div>
                    </Card>
                </section>

                {/* Turnaround */}
                <section>
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold flex items-center justify-center gap-3">
                            <Clock className="w-8 h-8 text-indigo-400" />
                            Turnaround & Workflow
                        </h2>
                    </div>

                    <Card className="max-w-3xl mx-auto">
                        <div className="space-y-0">
                            {[
                                { day: "Day 0", label: "Raw files dropped into guest folder", color: "bg-slate-500" },
                                { day: "Day 3–5", label: "First draft + thumbnail variants delivered", color: "bg-indigo-500" },
                                { day: "Day 6", label: "Trailer + captions + ManyChat flow ready", color: "bg-emerald-500" },
                                { day: "Day 7", label: "Final episode locked, scheduled to Later", color: "bg-green-500" }
                            ].map((step, i, arr) => (
                                <div key={i} className="flex items-center gap-4 py-4">
                                    <div className="flex flex-col items-center w-16 flex-shrink-0">
                                        <div className={`w-3 h-3 rounded-full ${step.color}`} />
                                        {i < arr.length - 1 && (
                                            <div className="w-0.5 h-8 bg-white/10 mt-1" />
                                        )}
                                    </div>
                                    <div className="flex-grow">
                                        <span className="text-sm font-bold text-white/50 uppercase tracking-widest">{step.day}</span>
                                        <p className="text-lg font-semibold text-white">{step.label}</p>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-white/10 mt-4 pt-6 space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-white/60">Rush delivery (48 hr)</span>
                                <span className="font-bold text-amber-400">+25% surcharge</span>
                            </div>
                        </div>
                    </Card>
                </section>

                {/* What We Need From You */}
                <section className="pb-10">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold flex items-center justify-center gap-3">
                            <Send className="w-8 h-8 text-indigo-400" />
                            What We Need From You
                        </h2>
                    </div>

                    <Card className="max-w-3xl mx-auto">
                        <div className="space-y-0 divide-y divide-white/10">
                            {requirements.map((req, i) => (
                                <div key={i} className="flex items-start justify-between py-4 gap-4">
                                    <div className="flex items-center gap-3">
                                        <span className="bg-indigo-500/20 text-indigo-400 w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">
                                            {i + 1}
                                        </span>
                                        <span className="font-semibold text-white">{req.item}</span>
                                    </div>
                                    <span className="text-white/50 text-sm text-right">{req.note}</span>
                                </div>
                            ))}
                        </div>

                        <div className="border-t border-white/10 mt-6 pt-6">
                            <p className="text-white/40 text-sm italic">
                                One open item to confirm — are long-form episodes still publishing on YouTube? The horizontal intro + chapter pack is scoped in by default. If the channel is moving audio-only, we'll swap that effort into additional clips at no extra cost.
                            </p>
                        </div>
                    </Card>
                </section>

            </main>
        </div>
    );
}
